import { ChapterEntry, ChapterId, CompletedTaskEntry, migrateTaskDataToLatest, SectionEntry, SectionId, TaskData, TaskEntry, TaskId, TaskType, VersionedTaskData } from "@/types/content-types";
import { createContext, MutableRefObject, useContext, useEffect, useSyncExternalStore } from "react";
import { useToast } from "../../hooks/use-toast";
import { supabase } from "@/lib/supabase/client";

export type LoadError = "failed_task_fetch" | "failed_data_fetch";
export type SaveError = "failed_task_save";
export type TaskStatus = "idle" | "loading" | "success" | "error";
export type DataStatus = "idle" | "loading" | "saving" | "success" | "error";

const completedTasksSelectColumns = "task_id, task_data, completed";
const taskSelectColumns = "id, section_id, type,content, has_access, effective_tier";
const sectionSelectColumns = "id, title, number, text, chapter_id, next_section, prev_section, has_access, effective_tier, completed_sections(user_id)";
const chapterSelectColumns = "id, title, number, next_chapter, prev_chapter, has_access, effective_tier, completed_chapters(user_id)";

// const TASK_TABLE_NAME = import.meta.env.VITE_TEST_TASKS === "true" ? "tasks_test" : "tasks_view";
const TASK_TABLE_NAME = "simple_tasks_view";
const COMPLETED_TASK_TABLE_NAME = "completed_tasks";

const derivedTaskStateKeys = [] as const satisfies readonly (keyof TaskState)[];
type DerivedTaskStateKeys = (typeof derivedTaskStateKeys)[number];
export type TaskState = {
    task: TaskEntry;
    data: TaskData;
    completed: boolean;
    loadError: LoadError;
    saveError: SaveError;
    save: ReturnType<typeof createSave>;
    refetchData: ReturnType<typeof createRefetchData>;
    taskStatus: TaskStatus;
    dataStatus: DataStatus;
};

type Subscribe<TID = void> = TID extends void
    ? (callback: () => void) => () => void
    : (id: TID, callback: () => void) => () => void;

type Snapshot<TValue> = () => TValue;
type SnapshotById<TID, TValue> = (id: TID) => TValue;
type Ensure<TResult> = () => Promise<TResult>;
type EnsureById<TID, TResult> = (id: TID) => Promise<TResult>;

export type TaskAPI = {
    subscribeToTask: Subscribe<TaskId>;
    subscribeToSection: Subscribe<SectionId>;
    subscribeToChapter: Subscribe<ChapterId>;

    subscribeToTasksInSection: Subscribe<SectionId>;
    subscribeToSectionsInChapter: Subscribe<ChapterId>;

    subscribeToCompletedSectionCount: Subscribe;

    getTaskSnapshot: SnapshotById<TaskId, TaskState>;
    getSectionSnapshot: SnapshotById<SectionId, SectionState>;
    getChapterSnapshot: SnapshotById<ChapterId, ChapterState>;

    getTasksInSectionSnapshot: SnapshotById<SectionId, TasksByType>;
    getSectionsInChapterSnapshot: SnapshotById<ChapterId, SectionsById>;

    getCompletedSectionCount: Snapshot<number>

    ensureTask: EnsureById<TaskId, TaskState>;
    ensureSection: EnsureById<SectionId, "success" | "error">;
    ensureChapter: EnsureById<ChapterId, "success" | "error">;
    ensureCompletedSectionCount: Ensure<"success" | "error">;
};

const derivedSectionStateKeys = [] as const satisfies readonly (keyof SectionState)[];
type DerivedSectionStateKeys = (typeof derivedSectionStateKeys)[number];
export type SectionState = {
    id: SectionId;
    section: SectionEntry;
    completedTaskCount: number;
    taskCount: number;
    taskIds: Record<TaskType, TaskId>;
    completed: boolean;
    ensured: boolean;
    completedInDB: boolean;
};

const derivedChapterStateKeys = [] as const satisfies readonly (keyof ChapterState)[];
type DerivedChapterStateKeys = (typeof derivedChapterStateKeys)[number];
export type ChapterState = {
    id: ChapterId;
    chapter: ChapterEntry;
    completedSectionCount: number;
    sectionCount: number;
    sectionIds: SectionId[];
    completed: boolean;
    ensured: boolean;
    completedInDB: boolean;
};

export type TasksById = Map<TaskId, TaskState>;
export type SectionsById = Map<SectionId, SectionState>;
export type ChaptersById = Map<ChapterId, ChapterState>;

type ListenerCallback = () => void;
type Listeners = ListenerCallback[];
type ListenersById<TID> = Map<TID, ListenerCallback[]>;

export type TaskIdsByType = Record<TaskType, TaskId>;
export type TasksByType = Record<TaskType, TaskState>;
export type TaskListeners = ListenersById<TaskId>;
export type TasksInSectionListeners = ListenersById<SectionId>;
export type SectionListeners = ListenersById<SectionId>;
export type SectionsInChapterListeners = ListenersById<SectionId>;
export type ChapterListeners = ListenersById<SectionId>;
export type CompletedSectionsCountListeners = Listeners;

export type SaveQueues = Map<TaskId, CompletedTaskEntry>;

type StateSetter<TID, TState> = (id: TID, patch: Partial<TState>) => void;
type TaskStateSetter = StateSetter<TaskId, TaskState>;

const EMPTY_ARRAY: [] = [];
const EMPTY_MAP: Map<never, never> = new Map<never, never>();

const EMPTY_TASKS_BY_TYPE: TasksByType = {
    chapter_insights: undefined,
    learn: undefined,
    practice: undefined,
    read: undefined,
    reflect: undefined,
    test: undefined,
    simple_explore: undefined,
    simple_practise: undefined,
    simple_test_and_reflect: undefined,
};

const EMPTY_TASK_IDS_BY_TYPE: TaskIdsByType = {
    chapter_insights: undefined,
    learn: undefined,
    practice: undefined,
    read: undefined,
    reflect: undefined,
    test: undefined,
    simple_explore: undefined,
    simple_practise: undefined,
    simple_test_and_reflect: undefined,
};

const EMPTY_DATA_ENTRY: CompletedTaskEntry = {
    task_data: { version: 2, data: {} },
    task_id: undefined,
    completed: false,
};

export const EMPTY_TASK_STATE: TaskState = {
    // section: undefined,
    completed: false,
    data: undefined,
    loadError: undefined,
    saveError: undefined,
    taskStatus: "idle",
    dataStatus: "idle",
    task: undefined,
    save: async () => EMPTY_TASK_STATE,
    refetchData: async () => { },
};

export const EMPTY_SECTION_STATE: SectionState = {
    id: undefined,
    section: undefined,
    taskCount: 0,
    completedTaskCount: 0,
    taskIds: EMPTY_TASK_IDS_BY_TYPE,
    completed: false,
    ensured: false,
    completedInDB: false,
};

export const EMPTY_CHAPTER_STATE: ChapterState = {
    id: undefined,
    chapter: undefined,
    completed: false,
    completedSectionCount: 0,
    sectionCount: 0,
    sectionIds: EMPTY_ARRAY,
    ensured: false,
    completedInDB: false,
};

async function fetchTaskEntry(taskId: TaskId): Promise<TaskEntry> {
    if (taskId == null) throw new Error("taskId is nullish");

    const { data, error } = await supabase.from(TASK_TABLE_NAME)
        .select(taskSelectColumns)
        .eq("id", taskId)
        .limit(1);

    if (error || !data?.length) {
        console.error("error fetching tasks", error);
        throw (error);
    }

    const taskEntry = data[0] as TaskEntry;

    return taskEntry;
}

async function getTaskEntriesBySection(sectionId: SectionId): Promise<TaskEntry[]> {
    if (sectionId == null) throw new Error("sectionId is nullish");

    const { data, error } = await supabase.from(TASK_TABLE_NAME)
        .select(taskSelectColumns)
        .eq("section_id", sectionId);

    if (error) {
        console.error(`error fetching tasks for section ${sectionId}`, error);
        throw (error);
    }
    if (!data?.length) {
        console.warn(`no tasks returned for section ${sectionId}`, error);
    }

    return data as TaskEntry[];
}

// async function getCompletedTaskEntries(taskIds: TaskId[], userId: string): Promise<CompletedTaskEntry[]> {
//     const { data, error } = await supabase
//         .from("completed_tasks")
//         .select("task_id, task_data, completed")
//         .eq("user_id", userId)
//         .in("task_id", taskIds);

//     if (error || !data?.length) {
//         throw error;
//     }

//     const migratedData = data.map(data => migrateTaskDataToLatest(data.task_data as VersionedTaskData));

//     const entries: CompletedTaskEntry[] = migratedData.map((mData, index) => ({
//         ...data[index],
//         task_data: mData,
//     }));

//     return entries;
// }

async function fetchTaskCompletion(taskId: TaskId, userId: string): Promise<CompletedTaskEntry> {
    const { data, error } = await supabase
        .from(COMPLETED_TASK_TABLE_NAME)
        .select(completedTasksSelectColumns)
        .eq("user_id", userId)
        .eq("task_id", taskId)
        .limit(1);

    if (error) {
        throw error;
    }

    if (!data?.length) {
        return { ...EMPTY_DATA_ENTRY, task_id: taskId };
    }

    const userData = migrateTaskDataToLatest(data[0].task_data as VersionedTaskData);

    const entry: CompletedTaskEntry = {
        ...data[0],
        task_data: userData,
    };

    return entry;
}


type CreateApiProps = {
    userId: string,
    saveQueues: MutableRefObject<SaveQueues>,

    taskStates: MutableRefObject<TasksById>,
    sectionStates: MutableRefObject<SectionsById>,
    chapterStates: MutableRefObject<ChaptersById>,

    completedSectionCount: MutableRefObject<number>,

    taskListeners: MutableRefObject<TaskListeners>,
    sectionListeners: MutableRefObject<SectionListeners>,
    chapterListeners: MutableRefObject<SectionListeners>,

    tasksInSectionListeners: MutableRefObject<TasksInSectionListeners>,
    sectionsInChapterListeners: MutableRefObject<TasksInSectionListeners>,

    completedSectionCountListeners: MutableRefObject<CompletedSectionsCountListeners>,
};

const EMPTY_API_OBJECT: TaskAPI = {
    subscribeToTask: () => () => { },
    subscribeToSection: () => () => { },
    subscribeToChapter: () => () => { },

    subscribeToTasksInSection: () => () => { },
    subscribeToSectionsInChapter: () => () => { },

    subscribeToCompletedSectionCount: () => () => { },

    getTaskSnapshot: () => EMPTY_TASK_STATE,
    getSectionSnapshot: () => EMPTY_SECTION_STATE,
    getChapterSnapshot: () => EMPTY_CHAPTER_STATE,

    getTasksInSectionSnapshot: () => EMPTY_TASKS_BY_TYPE,
    getSectionsInChapterSnapshot: () => EMPTY_MAP,

    getCompletedSectionCount: () => 0,

    ensureTask: async () => EMPTY_TASK_STATE,
    ensureSection: async () => "success",
    ensureChapter: async () => "success",
    ensureCompletedSectionCount: async () => "success",
};

function createSubscribeById<TID>(listeners: MutableRefObject<ListenersById<TID>>) {
    return function subscribe(id: TID, callback: ListenerCallback) {
        if (!listeners.current.has(id)) listeners.current.set(id, []);
        const byId = listeners.current.get(id);
        byId.push(callback);
        return () => {
            const index = byId.indexOf(callback);
            if (index >= 0) byId.splice(index, 1);
        };
    };
}

function createSubscribe(listeners: MutableRefObject<Listeners>) {
    return function subscribe(callback: ListenerCallback) {
        listeners.current.push(callback);
        return () => {
            const index = listeners.current.indexOf(callback);
            if (index >= 0) listeners.current.splice(index, 1);
        };
    };
}

function createGetState<TID, TState>(states: MutableRefObject<Map<TID, TState>>, defaultValue: TState) {
    return function getState(id: TID): TState {
        return states.current.get(id) ?? defaultValue;
    };
}

type Cache<TID, TData> = Map<TID, { isStale: boolean, data: TData }>;
function createCache<TID, TData>(): Cache<TID, TData> {
    return new Map();
}

function markAsStale<TID>(cache: Cache<TID, unknown>, id: TID) {
    if (id && cache.has(id)) {
        cache.get(id).isStale = true;
    }
}

export function createApi(
    {
        userId,
        taskStates,
        saveQueues,
        taskListeners,
        sectionListeners,
        chapterListeners,
        sectionStates,
        chapterStates,
        tasksInSectionListeners,
        sectionsInChapterListeners,
        completedSectionCountListeners,
        completedSectionCount,
    }: CreateApiProps
): TaskAPI {
    if (!userId) return EMPTY_API_OBJECT;

    const liveTaskStates: TasksById = new Map();
    const liveSectionStates: SectionsById = new Map();
    const liveChapterStates: ChaptersById = new Map();

    const tasksInSectionCache = createCache<SectionId, TasksByType>();
    const sectionsInChapterCache = createCache<ChapterId, SectionsById>();

    function emitTask(id: TaskId) {
        taskListeners.current.get(id)?.forEach(callback => callback());
    }

    function emitSection(id: SectionId) {
        sectionListeners.current.get(id)?.forEach(callback => callback());
    }

    function emitChapter(id: SectionId) {
        chapterListeners.current.get(id)?.forEach(callback => callback());
    }

    function emitCompletedSectionCount() {
        completedSectionCountListeners.current.forEach(callback => callback());
    }

    const hasTaskState = (id: TaskId) => taskStates.current.has(id);
    const hasSectionState = (id: SectionId) => sectionStates.current.has(id);
    const hasChapterState = (id: SectionId) => chapterStates.current.has(id);

    const getTaskState = createGetState(taskStates, EMPTY_TASK_STATE);
    const getSectionState = createGetState(sectionStates, EMPTY_SECTION_STATE);
    const getChapterState = createGetState(chapterStates, EMPTY_CHAPTER_STATE);

    function setTaskStates(patches: Array<{ id: TaskId; patch: Partial<Omit<TaskState, DerivedTaskStateKeys>> }>) {
        const sectionCompletionDeltas = new Map<SectionId, number>();

        for (const { id, patch } of patches) {
            if (id == null) continue;

            const prev = getTaskState(id);
            const next: TaskState = {
                ...prev,
                ...patch,
                completed: prev.completed || patch?.completed,
            };

            const sectionId = next.task?.section_id;

            if (next?.completed && !prev.completed) {
                sectionCompletionDeltas.set(sectionId, (sectionCompletionDeltas.get(sectionId) ?? 0) + 1);
            }

            Object.assign(liveTaskStates.get(id), next);
            taskStates.current.set(id, next);

            if (next.task) registerSectionTask(sectionId, next.task.type, next.task.id);
        }

        // Batch section state updates and cache invalidations
        const affectedSectionIds = new Set(patches.map(({ id }) => getTaskState(id)?.task?.section_id).filter(Boolean));

        for (const sectionId of affectedSectionIds) {
            const delta = sectionCompletionDeltas.get(sectionId) ?? 0;
            if (delta > 0) {
                setSectionState(sectionId, {
                    completedTaskCount: liveSectionStates.get(sectionId).completedTaskCount + delta,
                });
            }
            markAsStale(tasksInSectionCache, sectionId);
        }

        // Batch emit
        for (const { id } of patches) {
            if (id != null) emitTask(id);
        }
    }

    // Backwards-compatible single-state wrapper
    function setTaskState(id: TaskId, patch: Partial<Omit<TaskState, DerivedTaskStateKeys>>) {
        setTaskStates([{ id, patch }]);
    }

    function setSectionStates(patches: Array<{ id: SectionId; patch: Partial<Omit<SectionState, DerivedSectionStateKeys>> }>) {
        const chapterCompletionDeltas = new Map<ChapterId, number>();
        let newlyCompletedCount = 0;

        for (const { id, patch } of patches) {
            if (id == null) continue;

            let next: SectionState = null;
            let prev: SectionState = null;

            if (!hasSectionState(id)) {
                next = {
                    ...EMPTY_SECTION_STATE,
                    ...patch,
                    id: id,
                };
            } else {
                prev = getSectionState(id);
                next = {
                    ...prev,
                    ...patch,
                    completed: (prev.completed || patch.completed) ?? false,
                };
            }

            const alreadyCompleted = next.completed;

            // Instant local estimate so the UI (progress bar, unlocking the next
            // section) doesn't wait on a network round trip. The backend
            // (internal.is_section_complete + the after_task_completed_check_section
            // trigger on completed_tasks) is the actual source of truth for
            // persistence and will correct this on the next full section/chapter
            // fetch if it ever disagrees — acceptable since section completion
            // isn't safety/security critical, and createSave rolls back `completed`
            // on a failed save so this can't drift from a task that didn't persist.
            if (!next.completed && next.ensured && next.taskCount > 0 && next.completedTaskCount >= next.taskCount) {
                next.completed = true;
            }

            const chapterId = next.section?.chapter_id;

            // The chapter's own completed-section count should reflect every section
            // we now know is complete, whether we just learned that from the DB
            // (ensureSection/ensureChapter hydrating completedInDB) or it was
            // completed live this session.
            if (next?.completed && !prev?.completed) {
                chapterCompletionDeltas.set(chapterId, (chapterCompletionDeltas.get(chapterId) ?? 0) + 1);
            }

            // The global completedSectionCount must only react to LIVE completions —
            // otherwise every section hydrated as already-complete from the DB
            // (ensureSection/ensureChapter reading completedInDB) would call the
            // completed section count listeners.
            if (next.completed && !alreadyCompleted) {
                newlyCompletedCount++;
            }

            if (!liveSectionStates.has(id)) liveSectionStates.set(id, { ...getSectionState(id) });
            Object.assign(liveSectionStates.get(id), next);

            sectionStates.current.set(id, next);
            registerChapterSection(chapterId, id);
        }

        // Batch chapter state updates and cache invalidations
        const affectedChapterIds = new Set(patches.map(({ id }) => getSectionState(id)?.section?.chapter_id).filter(Boolean));

        for (const chapterId of affectedChapterIds) {
            const delta = chapterCompletionDeltas.get(chapterId) ?? 0;
            if (delta > 0) {
                setChapterState(chapterId, {
                    completedSectionCount: liveChapterStates.get(chapterId).completedSectionCount + delta,
                });
            }
            markAsStale(sectionsInChapterCache, chapterId);
        }

        // Batch emit
        for (const { id } of patches) {
            if (id != null) emitSection(id);
        }

        if (newlyCompletedCount > 0) {
            completedSectionCount.current += newlyCompletedCount;
            emitCompletedSectionCount();
        }
    }

    // Backwards-compatible single-state wrapper
    function setSectionState(id: SectionId, patch: Partial<Omit<SectionState, DerivedSectionStateKeys>>) {
        setSectionStates([{ id, patch }]);
    }

    function setChapterStates(patches: Array<{ id: ChapterId; patch: Partial<Omit<ChapterState, DerivedChapterStateKeys>> }>) {
        for (const { id, patch } of patches) {
            if (id == null) continue;

            let next: ChapterState;
            if (!hasChapterState(id)) {
                next = {
                    ...EMPTY_CHAPTER_STATE,
                    ...patch,
                    id: id,
                };
            } else {
                const prev = getChapterState(id);
                next = {
                    ...prev,
                    ...patch,
                    completed: (prev.completed || patch.completed) ?? false,
                };
            }

            // See the analogous comment in setSectionStates: recompute completion from
            // the merged state instead of only reacting to one specific patch shape.
            if (!next.completed && next.ensured && next.sectionCount > 0 && next.completedSectionCount >= next.sectionCount) {
                next.completed = true;
            }

            Object.assign(liveChapterStates.get(id), next);
            chapterStates.current.set(id, next);
        }

        // Batch emit
        for (const { id } of patches) {
            if (id != null) emitChapter(id);
        }
    }

    // Backwards-compatible single-state wrapper
    function setChapterState(id: ChapterId, patch: Partial<Omit<ChapterState, DerivedChapterStateKeys>>) {
        setChapterStates([{ id, patch }]);
    }

    function registerSectionTask(sectionId: SectionId, taskType: TaskType, taskId: TaskId) {
        if (sectionId == null || taskId == null) return;

        const prevIds = getSectionState(sectionId).taskIds;

        if (prevIds[taskType] === taskId) return;

        const nextIds: SectionState["taskIds"] = {
            ...prevIds,
            [taskType]: taskId,
        };

        // Recounting all is inefficient but it works for now
        const completedCount = Object.values(nextIds).reduce((count, taskId) => {
            if (getTaskState(taskId)?.completed) count++;
            return count;
        }, 0);
        const count = Object.values(nextIds).filter(id => id != null).length;

        setSectionState(sectionId, {
            taskIds: nextIds,
            completedTaskCount: completedCount,
            taskCount: count,
        });

        markAsStale(tasksInSectionCache, sectionId);
    }

    function registerChapterSection(chapterId: ChapterId, sectionId: SectionId) {
        if (sectionId == null || chapterId == null) return;

        const prevIds = getChapterState(chapterId).sectionIds;

        if (prevIds.includes(sectionId)) return;

        const nextIds: ChapterState["sectionIds"] = [
            ...prevIds,
            sectionId,
        ].sort((a, b) => getSectionState(a).section.number - getSectionState(b).section.number);

        // The completed count is not set here since its set when the completed
        // field in section states are set to true
        const count = Object.values(nextIds).filter(id => id != null).length;

        setChapterState(chapterId, {
            sectionIds: nextIds,
            sectionCount: count,
        });

        markAsStale(tasksInSectionCache, sectionId);
    }


    const ensureTask: TaskAPI["ensureTask"] = async (taskId) => {
        if (taskId == null) return EMPTY_TASK_STATE;

        if (!liveTaskStates.has(taskId)) liveTaskStates.set(taskId, { ...getTaskState(taskId) });
        const liveState = liveTaskStates.get(taskId);

        if (!hasTaskState(taskId)) {
            setTaskState(taskId, {
                save: createSave(userId, taskId, liveState, setTaskState, saveQueues),
                refetchData: createRefetchData(userId, taskId, setTaskState),
            });
        }

        // Update task
        if (!liveState.task) {
            try {
                setTaskState(taskId, {
                    task: await fetchTaskEntry(taskId),
                    taskStatus: "success",
                    loadError: null,
                });
            } catch (error) {
                console.error("failed_task_fetch", error);
                setTaskState(taskId, {
                    taskStatus: "error",
                    loadError: "failed_task_fetch",
                });
                return liveState;
            }
        }
        // Update user data
        if (!liveState.data) {
            await liveState.refetchData();
        }
        return liveState;
    };

    const ensureSection: TaskAPI["ensureSection"] = async (sectionId) => {
        if (sectionId == null) return "success";

        if (!liveSectionStates.has(sectionId)) liveSectionStates.set(sectionId, { ...getSectionState(sectionId) });
        const liveState = liveSectionStates.get(sectionId);

        if (!liveState.id) {
            setSectionState(sectionId, {
                id: sectionId,
            });
        }

        if (!liveState.section) {
            const { data: sectionData, error: sectionsError } = await supabase
                .from("sections_view")
                .select(sectionSelectColumns)
                .eq("id", sectionId)
                .eq("completed_sections.user_id", userId)
                .limit(1);

            if (sectionsError) {
                console.error("error fetching section", sectionsError);
                return "error";
            }
            if (!sectionData || sectionData.length === 0) {
                console.error("section not found", sectionId);
                return "error";
            }
            const entry = sectionData[0];
            const completed = entry.completed_sections.length > 0;
            // This is so that the section object does not contain the unnecessary completed_section object
            delete entry.completed_sections;

            setSectionState(sectionId, {
                section: entry,
                completed: completed,
                completedInDB: completed,
            });
        }

        try {
            const tasks = await getTaskEntriesBySection(sectionId);
            tasks.forEach(task => {
                if (liveTaskStates.has(task.id)) return;
                if (!liveTaskStates.has(task.id)) liveTaskStates.set(task.id, { ...getTaskState(task.id) });

                setTaskState(task.id, {
                    save: createSave(userId, task.id, liveTaskStates.get(task.id), setTaskState, saveQueues),
                    refetchData: createRefetchData(userId, task.id, setTaskState),
                    task: task,
                    taskStatus: "success",
                    loadError: null,
                });
            });

            // TODO:
            // Currently, even if the task data is fetched here, the ensureTask function
            // still fetches the task data one by one, making this redundant.
            // Change it so that if the data is being fetched here,
            // the ensure call won't fetch it. Probably by using an inFlight list to
            // track which tasks are currently being fetched

            // const data = await getCompletedTaskEntries(tasks.map(task => task.id), userId);

            // data.forEach(data => {
            //     setTaskState(data.task_id, data);
            // });
        } catch (error) {
            console.error("failed to fetch tasks by section", error);
            return "error";
        }

        setSectionState(sectionId, {
            ensured: true,
        });

        return "success";
    };

    const ensureChapter: TaskAPI["ensureChapter"] = async (chapterId: SectionId) => {
        if (chapterId == null) return "success";

        if (!liveChapterStates.has(chapterId)) liveChapterStates.set(chapterId, { ...getChapterState(chapterId) });
        const liveState = liveChapterStates.get(chapterId);

        if (!liveState.id) {
            setChapterState(chapterId, {
                id: chapterId,
            });
        }
        if (!liveState.chapter) {
            const { data: chapterData, error: chaptersError } = await supabase
                .from("chapters_view")
                .select(chapterSelectColumns)
                .eq("id", chapterId)
                .eq("completed_chapters.user_id", userId)
                .limit(1);

            if (chaptersError) {
                console.error("error fetching chapter", chaptersError);
                return "error";
            }
            if (!chapterData || chapterData.length === 0) {
                console.error("chapter not found", chapterData);
                return "error";
            }

            const entry = chapterData[0];
            const completed = entry.completed_chapters.length > 0;
            // This is so that the chapters object does not contain the unnecessary completed_chapters object
            delete entry.completed_chapters;

            setChapterState(chapterId, {
                chapter: chapterData[0],
                completed: completed,
                completedInDB: completed,
            });
        }

        const { data: sectionEntries, error: sectionsError } = await supabase.from("sections_view")
            .select(sectionSelectColumns)
            .eq("chapter_id", chapterId)
            .eq("completed_sections.user_id", userId);

        if (sectionsError) {
            console.error(`error chapter sections for chapter ${chapterId}`, sectionsError);
            return "error";
        }

        setSectionStates(sectionEntries.map(entry => {
            const completed = entry.completed_sections.length > 0;

            // This is so that the section object does not contain the unnecessary completed_section object
            delete entry.completed_sections;

            return {
                id: entry.id,
                patch: {
                    id: entry.id,
                    section: entry,
                    completed: completed,
                    completedInDB: completed,
                }
            };
        }));

        setChapterState(chapterId, {
            ensured: true,
        });

        return "success";
    };

    const ensureCompletedSectionCount: TaskAPI["ensureCompletedSectionCount"] = async () => {
        const { data, error } = await supabase.from("completed_section_total_counts").select("count").eq("user_id", userId).limit(1);
        if (error) {
            console.error("error fetching completed section count", error);
            return "error";
        }

        completedSectionCount.current = data[0].count;

        return "success";
    };

    return {
        // SUBSCRIBE
        subscribeToTask: createSubscribeById(taskListeners),
        subscribeToSection: createSubscribeById(sectionListeners),
        subscribeToChapter: createSubscribeById(chapterListeners),

        subscribeToTasksInSection: createSubscribeById(tasksInSectionListeners),
        subscribeToSectionsInChapter: createSubscribeById(sectionsInChapterListeners),

        subscribeToCompletedSectionCount: createSubscribe(completedSectionCountListeners),

        // SNAPSHOT
        getTaskSnapshot: getTaskState,
        getSectionSnapshot: getSectionState,
        getChapterSnapshot: getChapterState,

        getTasksInSectionSnapshot(sectionId) {
            if (sectionId == null) return EMPTY_TASKS_BY_TYPE;

            const cache = tasksInSectionCache.get(sectionId);
            const isStale = cache?.isStale ?? true;

            if (!isStale) return cache.data;

            const next = Object.fromEntries(Object.entries(
                getSectionState(sectionId).taskIds).map(([taskType, taskId]) => {
                    return [taskType, getTaskState(taskId)];
                })
            ) as TasksByType;
            tasksInSectionCache.set(sectionId, { data: next, isStale: false });
            return next;
        },

        getSectionsInChapterSnapshot(chapterId) {
            if (chapterId == null) return EMPTY_MAP;

            const cache = sectionsInChapterCache.get(chapterId);
            const isStale = cache?.isStale ?? true;

            if (!isStale) return cache.data;

            const next = new Map(getChapterState(chapterId)
                .sectionIds
                .map(sectionId => {
                    const state = getSectionState(sectionId);
                    return [state.id, state];
                })
            );
            sectionsInChapterCache.set(chapterId, { data: next, isStale: false });
            return next;
        },

        getCompletedSectionCount() {
            return completedSectionCount.current;
        },

        // ENSURE
        ensureTask,
        ensureSection,
        ensureChapter,

        ensureCompletedSectionCount,
    };
}

function createRefetchData(
    userId: string,
    taskId: TaskId,
    setTaskState: TaskStateSetter
) {
    return async function refetchData() {
        try {
            const entry = await fetchTaskCompletion(taskId, userId);

            setTaskState(taskId, {
                data: entry.task_data,
                completed: entry.completed ?? false,
                dataStatus: "success",
                loadError: null,
            });
        } catch (error) {
            console.error("failed_data_fetch", error);
            setTaskState(taskId, {
                dataStatus: "error",
                loadError: "failed_data_fetch",
            });
        }
    };
}

function createSave(
    userId: string,
    id: TaskId,
    liveState: TaskState,
    setState: TaskStateSetter,
    saveQueue: MutableRefObject<Map<TaskId, CompletedTaskEntry>>,
) {
    return async ({ data, completed }: { data: TaskData, completed: boolean }): Promise<TaskState> => {
        const prevCompleted = liveState.completed;
        const prevData = liveState.data;

        // Don't update the completed value if it would be set to false
        // The db defaults to false, and completed tasks should not be marked as incomplete
        setState(id, {
            completed: liveState.completed || completed,
            data: data,
        });

        saveQueue.current.set(id, {
            task_id: id,
            task_data: data,
            completed: liveState.completed ?? false,
        });

        if (liveState.dataStatus === "saving") return liveState;

        setState(id, {
            dataStatus: "saving",
        });

        let currentEntry: CompletedTaskEntry = saveQueue.current.get(id);
        do {
            saveQueue.current = saveQueue.current.set(id, null);

            const { error } = await supabase.from(COMPLETED_TASK_TABLE_NAME).upsert({
                ...currentEntry,
                user_id: userId,
            });

            if (error) {
                // Roll back the optimistic update: since nothing was actually
                // persisted, anything derived from `completed` client-side (e.g.
                // section progress/completion) would otherwise be wrong until the
                // next full reload.
                setState(id, {
                    completed: prevCompleted,
                    data: prevData,
                    dataStatus: "error",
                    saveError: "failed_task_save",
                });
                break;
            } else {
                setState(id, {
                    saveError: null,
                });
            }

            currentEntry = saveQueue.current.get(id);
        } while (currentEntry);

        if (liveState.dataStatus !== "error") {
            setState(id, {
                dataStatus: "success",
            });
        }

        return liveState;
    };
}

function toastLoadError(toast, loadError: LoadError) {
    switch (loadError) {
        case "failed_task_fetch":
            toast({
                title: `Lyckades inte hämta uppgift`,
                description: "Vänligen försök igen senare",
                variant: "destructive"
            });
            break;
        case "failed_data_fetch":
            toast({
                title: `Lyckades inte hämta dina svar`,
                description: "Vänligen försök igen senare",
                variant: "destructive"
            });
            break;
    }
}

export const AppContentAPIContext = createContext<TaskAPI>(null);

export function useTask({ taskId }: { taskId: TaskId }): TaskState {
    const { toast } = useToast();

    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useTask must be used within AppContentProvider");

    const state = useSyncExternalStore(
        callback => api.subscribeToTask(taskId, callback),
        () => api.getTaskSnapshot(taskId),
    );

    useEffect(() => {
        api.ensureTask(taskId).then((state) => {
            if (state.taskStatus === "error") {
                toastLoadError(toast, state.loadError);
            }
        });
    }, [api, taskId, toast]);

    return state;
}

export function useSection({ sectionId }: { sectionId: SectionId }): SectionState {
    const { toast } = useToast();
    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useSection must be used within AppContentProvider");

    const state = useSyncExternalStore(
        callback => api.subscribeToSection(sectionId, callback),
        () => api.getSectionSnapshot(sectionId),
    );

    useEffect(() => {
        api.ensureSection(sectionId).then((state) => {
            if (state === "error") {
                toast({
                    title: `Lyckades inte hämta uppgifter`,
                    description: "Vänligen försök igen senare",
                    variant: "destructive"
                });
            }
        });
    }, [api, sectionId, toast]);

    return state;
}

export function useChapter({ chapterId }: { chapterId: ChapterId }): ChapterState {
    const { toast } = useToast();
    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useSection must be used within AppContentProvider");

    const state = useSyncExternalStore(
        callback => api.subscribeToChapter(chapterId, callback),
        () => api.getChapterSnapshot(chapterId),
    );

    useEffect(() => {
        api.ensureChapter(chapterId).then((state) => {
            if (state === "error") {
                toast({
                    title: `Lyckades inte hämta kapitel`,
                    description: "Vänligen försök igen senare",
                    variant: "destructive"
                });
            }
        });
    }, [api, chapterId, toast]);

    return state;
}


export function useTasksInSection({ sectionId }: { sectionId: SectionId }): TasksByType {
    const { toast } = useToast();
    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useTasksInSection must be used within AppContentProvider");

    const states = useSyncExternalStore(
        callback => api.subscribeToTasksInSection(sectionId, callback),
        () => api.getTasksInSectionSnapshot(sectionId),
    );

    useEffect(() => {
        api.ensureSection(sectionId)
            .then((state) => {
                if (state === "error") {
                    toast({
                        title: `Lyckades inte hämta underrubrik`,
                        description: "Vänligen försök igen senare",
                        variant: "destructive"
                    });
                }
            });
    }, [api, sectionId, toast]);

    return states;
}

export function useSectionsInChapter({ chapterId }: { chapterId: ChapterId }) {
    const { toast } = useToast();
    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useSectionsInChapter must be used within AppContentProvider");

    const state = useSyncExternalStore(
        callback => api.subscribeToSectionsInChapter(chapterId, callback),
        () => api.getSectionsInChapterSnapshot(chapterId),
    );

    useEffect(() => {
        api.ensureChapter(chapterId).then((state) => {
            if (state === "error") {
                toast({
                    title: `Lyckades inte hämta underrubriker`,
                    description: "Vänligen försök igen senare",
                    variant: "destructive"
                });
            }
        });
    }, [api, chapterId, toast]);

    return state;
}

export function useCompletedSectionCount() {
    const api = useContext(AppContentAPIContext);
    if (!api) throw new Error("useCompletedSectionCount must be used within TaskProvider");

    const state = useSyncExternalStore(
        callback => api.subscribeToCompletedSectionCount(callback),
        () => api.getCompletedSectionCount(),
    );

    return state;
}